import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = ({ contactType = 'general', title, subtitle }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim() || formData.first_name.length < 2) {
      newErrors.first_name = 'Prénom requis (min. 2 caractères)';
    }
    if (!formData.last_name.trim() || formData.last_name.length < 2) {
      newErrors.last_name = 'Nom requis (min. 2 caractères)';
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email valide requis';
    }
    if (!formData.phone.trim() || formData.phone.length < 8) {
      newErrors.phone = 'Numéro de téléphone requis';
    }
    if (!formData.message.trim() || formData.message.length < 10) {
      newErrors.message = 'Message requis (min. 10 caractères)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        contact_type: contactType,
      });
      
      setIsSuccess(true);
      toast.success('Message envoyé avec succès ! Nous vous recontacterons sous 24h.');
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      
      if (error.response?.status === 422 && error.response?.data?.detail) {
        const backendErrors = {};
        error.response.data.detail.forEach((err) => {
          const field = err.loc?.[1];
          if (field) {
            backendErrors[field] = err.msg;
          }
        });
        setErrors(backendErrors);
        toast.error('Veuillez corriger les erreurs dans le formulaire');
      } else {
        toast.error('Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  if (isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card rounded-2xl p-8 md:p-12 text-center"
        data-testid="contact-form-success"
      >
        <motion.div 
          className="w-20 h-20 mx-auto mb-6 rounded-full bg-sage/20 flex items-center justify-center"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        >
          <CheckCircle className="text-sage" size={40} />
        </motion.div>
        <motion.h3 
          className="font-heading text-2xl md:text-3xl text-white mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          Message envoyé
        </motion.h3>
        <motion.p 
          className="font-body text-gray-400 mb-8 max-w-md mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          Merci pour votre engagement. Je vous recontacte sous 24h 
          pour planifier notre premier échange.
        </motion.p>
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setIsSuccess(false)}
          data-testid="send-another-message-btn"
          className="bg-transparent border border-sage/50 text-sage font-body font-medium px-8 py-3 rounded-full hover:bg-sage/10 transition-all duration-300"
        >
          Envoyer un autre message
        </motion.button>
      </motion.div>
    );
  }

  const inputVariants = {
    focus: { scale: 1.01, borderColor: 'rgba(143, 188, 143, 0.5)' },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="glass-card rounded-2xl p-8 md:p-12"
      data-testid="contact-form-container"
    >
      {title && (
        <h3 className="font-heading text-2xl md:text-3xl text-white mb-2">
          {title}
        </h3>
      )}
      {subtitle && (
        <p className="font-body text-gray-400 mb-8">
          {subtitle}
        </p>
      )}

      <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div whileFocus="focus" variants={inputVariants}>
            <label htmlFor="first_name" className="block font-body text-sm text-gray-300 mb-2">
              Prénom *
            </label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              data-testid="input-first-name"
              className={`w-full bg-[#0A0A0A] border ${
                errors.first_name ? 'border-red-500' : 'border-white/10'
              } rounded-xl px-4 py-3.5 font-body text-white placeholder-gray-500 focus:outline-none focus:border-sage/50 transition-all duration-300`}
              placeholder="Votre prénom"
            />
            {errors.first_name && (
              <motion.p 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-sm text-red-400 font-body"
              >
                {errors.first_name}
              </motion.p>
            )}
          </motion.div>
          <div>
            <label htmlFor="last_name" className="block font-body text-sm text-gray-300 mb-2">
              Nom *
            </label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              data-testid="input-last-name"
              className={`w-full bg-[#0A0A0A] border ${
                errors.last_name ? 'border-red-500' : 'border-white/10'
              } rounded-xl px-4 py-3.5 font-body text-white placeholder-gray-500 focus:outline-none focus:border-sage/50 transition-all duration-300`}
              placeholder="Votre nom"
            />
            {errors.last_name && (
              <motion.p 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-sm text-red-400 font-body"
              >
                {errors.last_name}
              </motion.p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="email" className="block font-body text-sm text-gray-300 mb-2">
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              data-testid="input-email"
              className={`w-full bg-[#0A0A0A] border ${
                errors.email ? 'border-red-500' : 'border-white/10'
              } rounded-xl px-4 py-3.5 font-body text-white placeholder-gray-500 focus:outline-none focus:border-sage/50 transition-all duration-300`}
              placeholder="votre@email.com"
            />
            {errors.email && (
              <motion.p 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-sm text-red-400 font-body"
              >
                {errors.email}
              </motion.p>
            )}
          </div>
          <div>
            <label htmlFor="phone" className="block font-body text-sm text-gray-300 mb-2">
              Téléphone *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              data-testid="input-phone"
              className={`w-full bg-[#0A0A0A] border ${
                errors.phone ? 'border-red-500' : 'border-white/10'
              } rounded-xl px-4 py-3.5 font-body text-white placeholder-gray-500 focus:outline-none focus:border-sage/50 transition-all duration-300`}
              placeholder="+32 472 80 27 32"
            />
            {errors.phone && (
              <motion.p 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-sm text-red-400 font-body"
              >
                {errors.phone}
              </motion.p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="message" className="block font-body text-sm text-gray-300 mb-2">
            Votre situation / message *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={5}
            data-testid="input-message"
            className={`w-full bg-[#0A0A0A] border ${
              errors.message ? 'border-red-500' : 'border-white/10'
            } rounded-xl px-4 py-3.5 font-body text-white placeholder-gray-500 focus:outline-none focus:border-sage/50 transition-all duration-300 resize-none`}
            placeholder="Décrivez brièvement votre situation et vos objectifs..."
          />
          {errors.message && (
            <motion.p 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-sm text-red-400 font-body"
            >
              {errors.message}
            </motion.p>
          )}
        </div>

        <div className="pt-4">
          <motion.button
            type="submit"
            disabled={isSubmitting}
            data-testid="submit-contact-form"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full md:w-auto bg-sage text-black font-body font-semibold px-10 py-4 rounded-full hover:bg-sage-300 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Envoi en cours...
              </>
            ) : (
              <>
                <Send size={20} />
                Envoyer ma demande
              </>
            )}
          </motion.button>
        </div>

        <p className="font-body text-xs text-gray-500 mt-4">
          * Champs obligatoires. Vos données sont traitées de manière confidentielle.
        </p>
      </form>
    </motion.div>
  );
};

export default ContactForm;
